import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import Dropdown from 'flarum/common/components/Dropdown';
import Api from '../utils/Api';
import { trimNum, lessonDateParts, lessonTimeText } from '../utils/MyCardView';
// v0.19.22 — 后端错误文案的**共用入口** (三级兜底, 与本文件历史手抄版逐字等价)
import { errReason } from '../utils/FormPrimitives';

/**
 * v0.19.15 — 「学员自主约课」弹窗 (辉哥 10/27 拍板, 设计稿屏 4)
 * v0.19.16 — 「用哪张卡」从**只读字段**改成**下拉选择** (辉哥 10/28 09:1x 拍板「1，A方案」)
 *
 * ## v0.19.16 变了什么
 *   · 弹窗**自己**拉 `GET /my-cards` ⇒ 列出学员的**全部可用卡**, 让他自己选;
 *   · `vnode.attrs.card` **删掉** (「我的卡片」tab 的入口本批被删了 ⇒ 没有调用方了);
 *   · `vnode.attrs.date` = 预填上课日期 ('YYYY-MM-DD' 本地日历日; 不传 = 今天) ——
 *     日历右键入口传「日历当前查看的那天」, 约课管理入口不传;
 *   · 「已约课次」改成**跨卡汇总** (取消只认 lesson id, 不受换卡影响)。
 *
 * ## 两段式 (brief §6.5)
 *   ① 已约课次（可取消）—— 数据来自 `GET /my-cards` 的 `lessons[]` (F28: 不用新端点)
 *   ② 约新课次 —— 选日期 ⇒ 拉可约时段 (chip 网格) ⇒ 可选教练 ⇒ 提交
 *
 * ## ⚠️ 设计稿的**两处修正** (以 brief 为准, 见 brief §6.5)
 *   1. **只保留 chip 网格**作唯一的时段选择器 (原设计稿画了「时间段下拉 + chip 网格」两个,
 *      是重复控件) ⇒ 本文件**没有**任何时段下拉。
 *   2. 顶部**新增**「① 已约课次（可取消）」段 (原设计稿没画; 辉哥拍板了「学员可自取消」
 *      但没指定入口位置 ⇒ 定在弹窗内自闭环, 让本批可独立上线)。
 *
 * ## ⛔ 三个硬约束
 *   ① **不复用** `LessonEditorModal` (它要求选教室/教练/学员/卡, 校验规则完全不同,
 *      且是员工端语义 —— 学员端没有教室可选, 也没有「选学员」这一步)。
 *   ② 图标一律 **Font Awesome**, ⛔ 不用 emoji。
 *   ③ 「已约课次」只显示**学员自己约的**那一批 (`self_booked === true`):
 *      前台代约的课**不在这里**, 也不能自行取消 (后端 `CancelSelfBookedLessonController`
 *      会再判一次 —— 前端过滤只是体验, 后端才是硬约束)。
 *
 * ## ⛔ v0.19.16 的两条纪律
 *   ① `content()` **必须返回数组** (批 35 补正① 的 P0 修复 —— 改回外包 `<div>`
 *      = 底部「提交预约」按钮被推出视口, 学员点不到);
 *   ② 换卡 ⇒ **必须**清空已选时段与已选教练 (时段/教练都是**按校区**来的,
 *      不清就是拿旧校区的档去提交, 后端必 422)。
 *
 * ## 时间口径
 *   · 日期选择器的「今天」用**浏览器本地**日历日 (同 CalendarTab.localDateStr 的纪律:
 *     ⛔ 不用 toISOString(), 那是 UTC, 东八区凌晨会算成前一天)。
 *   · 可约窗口 = 今天起 14 天 (含今天) —— 与后端 `SelfBookingService::window()` 同口径。
 *   · `start_at` / `end_at` 是**本地墙上时刻字面量** ('YYYY-MM-DD HH:MM:SS'),
 *     与员工端 payload 同形。
 */
export default class SelfBookingModal extends Modal {
  oninit(vnode) {
    super.oninit(vnode);

    // ── v0.19.16 — 「用哪张卡」从**只读字段**改成**下拉选择** ──────────────────
    //   弹窗**自己**拉卡列表 (GET /my-cards), 让学员自己选用哪张卡约。
    //   ⇒ 以前「一张卡进、一个卡出」的 `vnode.attrs.card` 依赖已经**没有调用方**
    //     (「我的卡片」tab 的入口在本批被删掉了) ⇒ 这个参数直接删掉, 不留兼容分支。
    /** 学员的全部卡 (原始顺序, GET /my-cards 原样) */
    this.allCards = [];
    /** **可用**卡 (过滤 + 排序后) */
    this.cards = [];
    /** 当前选中的卡 (null = 还没选 / 一张都不可用) */
    this.selectedCard = null;
    /** 正在拉卡列表 */
    this.loadingCards = false;
    /** 拉卡列表失败的文案 (403 = 没权限, ⛔ 不许渲染成「你没有卡」) */
    this.cardsError = '';

    /** 父级回调: 约完/取消完之后让调用方刷新一次 */
    this.onBooked = vnode.attrs.onBooked || null;

    /** 上课日期 ('YYYY-MM-DD'), 默认今天 */
    // ⚠️ 调用方可以预填 (日历右键入口 = 日历当前查看的那天);
    //   形状不对 ⇒ 回落到今天 (⛔ 绝不把脏值喂给后端, 后端会 400「日期格式应为 YYYY-MM-DD」)。
    const preDate = vnode.attrs.date;
    this.date = preDate && /^\d{4}-\d{2}-\d{2}$/.test(preDate) ? preDate : this.todayStr();
    /** 可约时段 (来自 GET /lessons/self-booking-slots) */
    this.slots = [];
    /** 该校区的教练候选 (同一个响应里带回来) */
    this.coaches = [];
    /** 选中的课表节点 id */
    this.selectedNodeId = null;
    /** 选中的教练 id (null = 不指定, 由校区安排) */
    this.coachId = null;
    this.loading = false;
    this.saving = false;
    this.cancelling = false;
    this.error = '';
    this.okMsg = '';

    /** 「已约课次」列表 (本地副本; 取消成功后本地移除那一行) */
    this.booked = [];

    // v0.19.16 — 改成先拉卡列表 (它内部会选卡 ⇒ 触发 loadSlots)。
    //   ⛔ 「已约课次」必须等卡列表回来才有 lessons[] ⇒ initialBooked() 挪进 loadCards()。
    this.loadCards();
  }
  className() {
    // ⚠️ `ZS-SelfBookingModal` 是**样式作用域锁** —— less 里所有覆盖规则都挂在它下面。
    return 'ZS-SelfBookingModal';
  }
  title() {
    return m("span", null, m("i", {
      className: "fas fa-calendar-plus",
      style: "color:var(--zs-cyan-06b6d4, #06b6d4);margin-right:8px;"
    }), "\u81EA\u4E3B\u7EA6\u8BFE");
  }

  // ─── 数据 ──────────────────────────────────────────────────────────────

  /**
   * v0.19.16 — 拉**自己的全部卡**, 过滤出**可用卡**, 选第一张。
   *
   * ⚠️ 为什么弹窗自己拉 (`GET /my-cards`):
   *   「用哪张卡」改成下拉之后, 弹窗可能要给学员看**好几张**卡 ⇒ 必须自己拿列表。
   *   学员有 `scheduler.viewMyCards` (批 35 已有的门), 学员端那个端点按 actor.id 行级锁死。
   *
   * ⚠️ **非数组** (403/401 的 `{success:false,…}` 信封) 一律当「拉不到」, ⛔ 不当「没有卡」——
   *   那是旧端点的静默失败模式 (同 MyCardsTab.load 的口径)。
   */
  async loadCards() {
    this.loadingCards = true;
    this.cardsError = '';
    m.redraw();
    try {
      const res = await Api.list('my-cards');
      // ⚠️ **非数组** = 403/401 的 `{success:false,…}` 信封 ⇒ 走 `cardsError` 分支,
      //   ⛔ 绝不渲染成「你没有可自主约课的卡」 (那是把「没权限」说成「没卡」, 见 brief §9)。
      if (!Array.isArray(res)) {
        this.allCards = [];
        this.cards = [];
        this.selectedCard = null;
        this.slots = [];
        this.coaches = [];
        this.selectedNodeId = null;
        this.cardsError = '你没有「查看我的卡片」权限';
        return;
      }
      this.allCards = res;
      this.cards = this.allCards.filter(c => this.cardUsable(c));
      // 排序: 无限卡最前; 其余按剩余课时降序 (设计稿屏 3 的口径)
      this.cards.sort((a, b) => {
        const au = a.is_unlimited ? 1 : 0;
        const bu = b.is_unlimited ? 1 : 0;
        if (au !== bu) return bu - au;
        return (b.remaining_lessons || 0) - (a.remaining_lessons || 0);
      });
      // 已选中的卡若还在可用列表里 ⇒ 保持; 否则回落到第一张
      if (!this.selectedCard || !this.cards.some(c => c.id === this.selectedCard.id)) {
        this.selectCard(this.cards[0] || null);
      }
      // 「已约课次」是**跨卡汇总** ⇒ 必须等卡列表回来才有 lessons[]
      this.booked = this.initialBooked();
    } catch (e) {
      this.allCards = [];
      this.cards = [];
      this.selectedCard = null;
      this.slots = [];
      this.coaches = [];
      this.selectedNodeId = null;
      this.cardsError = (e && e.status) === 403 ? '你没有「查看我的卡片」权限' : '加载失败, 请稍后重试';
    } finally {
      this.loadingCards = false;
      m.redraw();
    }
  }

  /**
   * 这张卡**能不能用来自主约课** —— 逐字对齐后端 `StudentCard::isAvailable()` + 校区开关。
   *
   * ⚠️ `is_expired` / `is_trial_card` 用 payload 里**后端算好的**字段,
   *   ⛔ 前端不自己重算 (跟 `is_expired` 同源口径, `MyCardsPayloadBuilder` 类注释已写明)。
   */
  cardUsable(c) {
    if (!c) return false;
    if (c.status === 'refunded') return false; // ← 后端 isAvailable() 第 1 条
    if (c.is_activated !== true) return false; // ← 后端 isAvailable() 第 2 条
    if (c.is_expired === true) return false; // ← 后端 isExpired() (payload 已算好)
    if (c.is_trial_card === true) return false; // ← 后端 isTrialCard() (体验卡不支持)
    if (c.school_allow_self_booking !== true) return false; // ← 校区开关
    if (c.school_id === null || c.school_id === undefined) return false; // 无校区的卡约不了课
    return true;
  }

  /**
   * 换卡。
   *
   * ⚠️ **换卡必须清空 `selectedNodeId` 和 `coachId`** —— 时段和教练都是**按校区**来的,
   *   不清就会拿旧校区的档去提交 (后端必 422)。
   */
  selectCard(card) {
    const changed = !this.selectedCard || !card || this.selectedCard.id !== card.id;
    this.selectedCard = card;
    if (changed) {
      this.selectedNodeId = null;
      this.coachId = null;
      this.error = '';
      this.okMsg = '';
    }
    if (card) this.loadSlots();else {
      this.slots = [];
      this.coaches = [];
      this.selectedNodeId = null;
      m.redraw();
    }
  }

  /**
   * 「已约课次」= **所有卡**名下**学员自己约的**、还没上的课 (`status === 'scheduled'`)。
   *
   * ⚠️ 为什么筛 `self_booked`:
   *   设计稿屏 4 的说明文案写着「前台代约的课不在这里，不能自行取消」——
   *   ⇒ 这一段**只列自己约的**, 列表与文案才自洽。
   *   后端 `GET /my-cards` 的 `lessons[]` 里已带 `self_booked` (v0.19.15 加的)。
   *
   * ⚠️ v0.19.16 — 改成**跨卡汇总** (以前只有一张卡): 后端是按卡分组排的, 卡之间没有全局序
   *   ⇒ 合并后要自己按 `start_at` 重排一次。
   */
  initialBooked() {
    const out = [];
    (this.allCards || []).forEach(c => {
      (Array.isArray(c.lessons) ? c.lessons : []).forEach(l => {
        if (l && l.status === 'scheduled' && l.self_booked === true) {
          out.push(Object.assign({}, l));
        }
      });
    });
    out.sort((a, b) => String(a.start_at || '').localeCompare(String(b.start_at || '')));
    return out;
  }
  async loadSlots() {
    const schoolId = this.selectedCard ? this.selectedCard.school_id : null;
    if (!schoolId) {
      this.slots = [];
      this.coaches = [];
      m.redraw();
      return;
    }
    this.loading = true;
    this.error = '';
    m.redraw();
    try {
      const r = await Api.listSelfBookingSlots({
        school_id: schoolId,
        date: this.date
      });
      this.slots = r && Array.isArray(r.slots) ? r.slots : [];
      this.coaches = r && Array.isArray(r.coaches) ? r.coaches : [];
      // 换日期之后, 原来选中的那一档可能已经不可约了 ⇒ 清掉选择
      // (否则用户会拿着一个已占用的档去提交, 后端必然 422)
      const still = this.slots.find(s => s.node_id === this.selectedNodeId && s.available);
      if (!still) this.selectedNodeId = null;
    } catch (e) {
      this.slots = [];
      this.coaches = [];
      this.selectedNodeId = null;
      this.error = this.errorText(e);
    } finally {
      this.loading = false;
      m.redraw();
    }
  }
  onDateChange(e) {
    const v = e && e.target ? e.target.value : '';
    if (!v) return;
    this.date = v;
    this.loadSlots();
  }
  onSlotPick(slot) {
    if (!slot || !slot.available) return;
    this.selectedNodeId = slot.node_id;
    this.okMsg = '';
    m.redraw();
  }
  onCoachPick(id) {
    this.coachId = id;
    m.redraw();
  }

  // ─── 提交 / 取消 ───────────────────────────────────────────────────────

  async submit() {
    // v0.19.16 — 提交前**必须**有选中卡 (一张都不可用时按钮本来就禁用, 这里是第二道闸)
    if (!this.selectedCard) {
      this.error = '请选择一张可约的卡';
      m.redraw();
      return;
    }
    const slot = this.slots.find(s => s.node_id === this.selectedNodeId);
    if (!slot || !slot.available) {
      this.error = '请选择一个可约时段';
      m.redraw();
      return;
    }
    this.saving = true;
    this.error = '';
    this.okMsg = '';
    m.redraw();
    const startAt = this.date + ' ' + slot.start_time + ':00';
    const endAt = this.date + ' ' + slot.end_time + ':00';
    try {
      const res = await Api.selfBookLesson({
        student_card_id: this.selectedCard.id,
        schedule_node_id: slot.node_id,
        // ⛔ 不指定 ⇒ null ⇒ 后端「由校区安排」(lessons.coach_id 是 NOT NULL, 详见后端注释)
        coach_id: this.coachId || null,
        start_at: startAt,
        end_at: endAt
      });

      // 成功 ⇒ 本地补一行, 弹窗里立刻能看见新约的课
      const coachName = this.coachName(this.coachId);
      this.booked.push({
        id: res && res.id ? res.id : null,
        start_at: startAt,
        end_at: endAt,
        date: this.date,
        start_time: slot.start_time,
        end_time: slot.end_time,
        coach_id: this.coachId || null,
        coach_name: coachName,
        status: 'scheduled',
        self_booked: true
      });
      this.selectedNodeId = null;
      this.coachId = null;
      this.okMsg = '预约成功, 教练会收到通知';

      // 该档已被占用 ⇒ 重新拉一次时段 (否则用户会看到一个还能点的已占档)
      this.loadSlots();
      if (this.onBooked) this.onBooked();
    } catch (e) {
      this.error = this.errorText(e);
    } finally {
      this.saving = false;
      m.redraw();
    }
  }
  async cancelLesson(lesson) {
    const dp = lessonDateParts(lesson.start_at);
    const when = dp.d1 + ' ' + lessonTimeText(lesson);
    if (!confirm('确认取消 ' + when + ' 这一节课?')) return;
    this.cancelling = true;
    this.error = '';
    this.okMsg = '';
    m.redraw();
    try {
      await Api.cancelSelfBookedLesson(lesson.id);
      this.booked = this.booked.filter(l => l.id !== lesson.id);
      this.okMsg = '已取消该课次';
      if (this.onBooked) this.onBooked();
    } catch (e) {
      this.error = this.errorText(e);
    } finally {
      this.cancelling = false;
      m.redraw();
    }
  }

  // ─── 小工具 ───────────────────────────────────────────────────────────

  /** 浏览器**本地**日历日 → 'YYYY-MM-DD' (⛔ 不用 toISOString —— 那是 UTC) */
  localDateStr(d) {
    const dt = d instanceof Date ? d : new Date(d);
    if (isNaN(dt.getTime())) return '';
    return dt.getFullYear() + '-' + String(dt.getMonth() + 1).padStart(2, '0') + '-' + String(dt.getDate()).padStart(2, '0');
  }
  todayStr() {
    return this.localDateStr(new Date());
  }

  /** 可约窗口上界 = 今天 + 13 天 (含今天共 14 天) */
  maxDateStr() {
    const d = new Date();
    d.setDate(d.getDate() + 13);
    return this.localDateStr(d);
  }

  /** 422 的真实原因在 `response.errors[0].detail`, 不在 `e.message` (同 SchoolEditorModal) */
  errorText(e) {
    return errReason(e, '操作失败, 请稍后重试');
  }
  coachName(id) {
    if (!id) return null;
    const c = this.coaches.find(x => x.id === id);
    return c ? c.name : null;
  }

  /** 时段不可约的原因 (写进 chip 的 title) */
  slotReasonText(slot) {
    if (slot.available) return slot.label;
    if (slot.reason === 'occupied') return slot.label + ' · 该课槽已被占用';
    if (slot.reason === 'no_room') return slot.label + ' · 该时段没有空闲教室';
    if (slot.reason === 'past') return slot.label + ' · 该时段已经过了';
    return slot.label;
  }

  // ─── 渲染 ─────────────────────────────────────────────────────────────

  /** ① 已约课次 (可取消) */
  renderBooked() {
    return m("section", {
      className: "ZS-selfbook-sec"
    }, m("div", {
      className: "ZS-selfbook-sec-t"
    }, m("span", {
      className: "n"
    }, "1"), "\u5DF2\u7EA6\u8BFE\u6B21"), this.booked.length === 0 ? m("div", {
      className: "ZS-selfbook-empty"
    }, "\u8FD8\u6CA1\u6709\u9884\u7EA6\u8FC7\u8BFE\u6B21") : m("div", {
      className: "ZS-selfbook-booked"
    }, this.booked.map(l => {
      const dp = lessonDateParts(l.start_at);
      const coach = l.coach_name || this.coachName(l.coach_id);
      return m("div", {
        className: "ZS-selfbook-booked-row",
        key: l.id || l.start_at
      }, m("span", {
        className: "d"
      }, dp.d1), m("span", {
        className: "t"
      }, dp.d2, " ", lessonTimeText(l), " \xB7 ", coach || '未指定教练'), m("button", {
        className: "ZS-selfbook-cancel",
        disabled: this.cancelling,
        onclick: () => this.cancelLesson(l)
      }, m("i", {
        className: "fas fa-xmark"
      }), " \u53D6\u6D88"));
    })), m("div", {
      className: "ZS-selfbook-note"
    }, m("i", {
      className: "fas fa-circle-info"
    }), m("span", null, "\u53D6\u6D88\u4E0D\u6263\u8BFE\u65F6\uFF08\u8FD8\u6CA1\u4E0A\u8BFE\uFF0C\u672C\u6765\u5C31\u6CA1\u6263\uFF09\u3002\u524D\u53F0\u4EE3\u7EA6\u7684\u8BFE\u4E0D\u5728\u8FD9\u91CC\uFF0C\u4E0D\u80FD\u81EA\u884C\u53D6\u6D88\u3002")));
  }

  /** ② 约新课次 */
  renderNewBooking() {
    return m("section", {
      className: "ZS-selfbook-sec"
    }, m("div", {
      className: "ZS-selfbook-sec-t"
    }, m("span", {
      className: "n"
    }, "2"), "\u7EA6\u65B0\u8BFE\u6B21"), m("div", {
      className: "ZS-selfbook-label"
    }, "\u7528\u54EA\u5F20\u5361"), this.loadingCards ? m("div", {
      className: "ZS-selfbook-empty"
    }, "\u52A0\u8F7D\u4E2D\u2026") : this.cardsError ? m("div", {
      className: "ZS-selfbook-empty"
    }, this.cardsError) : this.cards.length === 0 ? m("div", {
      className: "ZS-selfbook-empty"
    }, "\u4F60\u6CA1\u6709\u53EF\u81EA\u4E3B\u7EA6\u8BFE\u7684\u5361\uFF08\u8BF7\u786E\u8BA4\u5361\u5DF2\u6FC0\u6D3B\u3001\u672A\u8FC7\u671F\uFF0C\u4E14\u6240\u5728\u6821\u533A\u5DF2\u5F00\u653E\u81EA\u4E3B\u7EA6\u8BFE\uFF09") :
    // ⚠️ 外层沿用 `.ZS-selfbook-static` 这个**既有** class (它带 `margin-bottom:14px`
    //   = 「用哪张卡」与「上课日期」之间的间距) ⇒ 本批 LESS **零新增规则**。
    //   core `Dropdown` 的根是 `.ButtonGroup` (inline-flex) ⇒ 包在 flex 容器里
    //   仍是按内容收缩, 与教练下拉同款 (⛔ 不用 display:contents)。
    m("div", {
      className: "ZS-selfbook-static"
    }, this.cardDropdown()), m("div", {
      className: "ZS-selfbook-label"
    }, "\u4E0A\u8BFE\u65E5\u671F"), m("input", {
      type: "date",
      className: "ZS-form-input ZS-selfbook-date",
      min: this.todayStr(),
      max: this.maxDateStr(),
      value: this.date,
      onchange: e => this.onDateChange(e)
    }), m("div", {
      className: "ZS-selfbook-label"
    }, "\u53EF\u7EA6\u65F6\u6BB5"), this.loading ? m("div", {
      className: "ZS-selfbook-empty"
    }, "\u52A0\u8F7D\u4E2D\u2026") : this.slots.length === 0 ? m("div", {
      className: "ZS-selfbook-empty"
    }, "\u8BE5\u65E5\u671F\u6CA1\u6709\u53EF\u7EA6\u65F6\u6BB5") : m("div", {
      className: "ZS-selfbook-slots"
    }, this.slots.map(s => m("button", {
      key: s.node_id,
      className: 'ZS-selfbook-slot' + (s.available ? '' : ' off') + (s.node_id === this.selectedNodeId ? ' on' : ''),
      disabled: !s.available,
      title: this.slotReasonText(s),
      onclick: () => this.onSlotPick(s)
    }, s.label))), m("div", {
      className: "ZS-selfbook-note"
    }, m("i", {
      className: "fas fa-circle-info"
    }), m("span", null, "\u5212\u6389\u7684\u65F6\u6BB5\u5DF2\u88AB\u5360\u7528\u3002\u53EF\u7EA6\u8303\u56F4\uFF1A\u4ECA\u5929\u8D77 14 \u5929\u5185\u3002")), m("div", {
      className: "ZS-selfbook-label",
      style: "margin-top:14px;"
    }, "\u9009\u6559\u7EC3\uFF08\u53EF\u9009\uFF09"), this.coachDropdown());
  }

  /**
   * v0.19.16 — 一张卡在「用哪张卡」下拉里的**显示文本**。
   *
   * ⚠️ **触发按钮的 label 与下拉项文本必须逐字一致** (设计稿屏 3 / 屏 3b) ⇒
   *   两处都走这一个方法, 不各写一份 (各写一份迟早分叉)。
   * ⚠️ `school_name` 由后端 `GET /my-cards` 带出来 (v0.19.16 新字段);
   *   缺失 (卡没校区 / 校区被软删) ⇒ 回落 `校区 #<id>`, ⛔ 绝不渲染 undefined。
   */
  cardLabel(c) {
    if (!c) return '';
    const name = c.name || '学员卡 #' + c.id;
    const school = c.school_name || '校区 #' + c.school_id;
    const remain = c.is_unlimited ? '不限次' : '剩 ' + trimNum(c.remaining_lessons) + ' 节';
    return name + ' · ' + school + ' · ' + remain;
  }

  /**
   * 「用哪张卡」—— Flarum **原生 Dropdown** (本仓口径: 下拉框统一用 core Dropdown)。
   *
   * ⚠️ 写法与上面 `coachDropdown()` **逐字同款**:
   *   core `Dropdown` + `Button` 数组 + `buttonClassName="ZS-form-input"`
   *   + `menuClassName="ZS-followup-dropdown-menu ZS-tf-menu"` ⇒ 零新增 LESS。
   * ⚠️ 所有兄弟 element 必须合并进**同一个数组** (共享 vnode.key 模式) ——
   *   写成两个子节点会让 Flarum Dropdown 只包出一个 <li>, core CSS 直接子选择器全废。
   * ⚠️ 下拉项文本是**一行** (⛔ 不放两个 span: core `Button` 会把 children 包进
   *   `.Button-label` (inline), `margin-left:auto` 不生效 ⇒ 右对齐的「剩 N 节」会塌)。
   */
  cardDropdown() {
    const label = this.selectedCard ? this.cardLabel(this.selectedCard) : '— 请选择一张卡';
    const items = [];
    this.cards.forEach(c => {
      items.push(m(Button, {
        key: 'card' + c.id,
        icon: "fas fa-id-card",
        active: !!this.selectedCard && this.selectedCard.id === c.id,
        onclick: () => this.selectCard(c)
      }, this.cardLabel(c)));
    });
    return m(Dropdown, {
      buttonClassName: "ZS-form-input",
      menuClassName: "ZS-followup-dropdown-menu ZS-tf-menu",
      icon: "fas fa-id-card"
      // ⚠️ core 的 `Dropdown` toggle **不自动带 `.Button` class** ⇒ 它是 `.ButtonGroup`
      //   (inline-flex) 的**弹性项** ⇒ 按 flex 规范 display 被 blockify 成 `block`
      //   ⇒ 图标 / 文字 / 箭头会**竖着堆叠** (实测截图: 变成一个窄条竖列)。
      //   本仓其它三处下拉是用**专用 class + `display:inline-flex !important`** 修的
      //   (`.ZS-school-dropdown-btn` / `.ZS-filter-dropdown-btn` / `.ZS-dash-range-btn`),
      //   但那要**新增 LESS 规则** —— 本批 X3 规定 less/forum.less 的 diff 必须是**纯删除**
      //   ⇒ 这里改用 core `Dropdown` 自带的 `buttonAttrs` 传**行内 style** (零 LESS 改动),
      //   效果与那三个 class 同款 (一行: 图标 · 文字 · 箭头)。
      ,
      buttonAttrs: {
        style: 'display:inline-flex !important; align-items:center; gap:7px; white-space:nowrap;'
      },
      label: label
    }, items);
  }

  /**
   * 选教练 —— Flarum **原生 Dropdown** (本仓口径: 下拉框统一用 core Dropdown)。
   *
   * ⚠️ 所有兄弟 element 必须合并进同一个 map 的 callback 里 (共享 vnode.key 模式) ——
   *   写成两个子节点会让 Flarum Dropdown 只包出一个 <li>, core CSS 直接子选择器全废
   *   (同 FixedLessonModal / LessonEditorModal 的同款注释)。
   */
  coachDropdown() {
    const current = this.coachId ? this.coaches.find(c => c.id === this.coachId) : null;
    const label = current ? current.name : '— 不指定则由校区安排';
    const items = [];
    items.push(m(Button, {
      key: "__none",
      active: !this.coachId,
      onclick: () => this.onCoachPick(null)
    }, "\u2014 \u4E0D\u6307\u5B9A\u5219\u7531\u6821\u533A\u5B89\u6392"));
    this.coaches.forEach(c => {
      items.push(m(Button, {
        key: 'c' + c.id,
        active: this.coachId === c.id,
        onclick: () => this.onCoachPick(c.id)
      }, c.name));
    });
    return m(Dropdown, {
      buttonClassName: "ZS-form-input",
      menuClassName: "ZS-followup-dropdown-menu ZS-tf-menu",
      label: label
    }, items);
  }
  content() {
    // ⚠️ v0.19.15 补正 ①: 这里**必须返回数组**, 不能外包一层 `<div>`。
    //   根因: Flarum 的 `.Modal-content` 是 `display:flex; flex-direction:column`,
    //   ⇒ 只有**它的直接子节点**才是 flex 子项, 才会被 flex 分配高度。
    //   原来这里包了一层无 class 的 `<div>` ⇒ `.ZS-selfbook-body` 变成那层的普通块级子项
    //   ⇒ 它上面的 `flex: 1 1 auto` **完全空转**;
    //   真正被 flex 分配高度的是那个包装层, 而它的 `min-height:auto`
    //   (自动最小尺寸 = 内容高度) 会把它顶住 ⇒ 永远不收缩 ⇒ 溢出被 `overflow:hidden` 裁掉
    //   ⇒ 底部「提交预约」按钮被推出视口 ⇒ 学员点不到 (P0)。
    //   返回数组 ⇒ body / footer 直接是 `.Modal-content` 的 flex 子项
    //   ⇒ body 的 `flex: 1 1 auto` + `min-height: 0` 才真正生效 (内容区自己滚, 底部按钮常驻)。
    //   (与 `MyCardDetailModal.inner()` 返回数组的做法同一条口径。)
    return [m("div", {
      className: "Modal-body ZS-selfbook-body",
      key: "b"
    }, this.renderBooked(), m("div", {
      className: "ZS-selfbook-divider"
    }), this.renderNewBooking(), this.error && m("div", {
      className: "ZS-newversion-error",
      style: "margin-top:10px;"
    }, m("i", {
      className: "fas fa-triangle-exclamation",
      style: "color:var(--zs-danger-ef4444, #ef4444);margin-right:6px;"
    }), this.error), this.okMsg && m("div", {
      className: "ZS-selfbook-ok",
      style: "margin-top:10px;"
    }, m("i", {
      className: "fas fa-circle-check",
      style: "color:var(--zs-success-34c759, #34c759);margin-right:6px;"
    }), this.okMsg)), m("div", {
      className: "Modal-footer ZS-selfbook-footer",
      key: "f"
    }, m(Button, {
      onclick: () => this.hide()
    }, m("i", {
      className: "fas fa-xmark"
    }), " \u53D6\u6D88"), m(Button, {
      className: "Button Button--primary"
      // v0.19.16 — 加上 `!this.selectedCard`: 一张可用卡都没有时**必须**禁用提交
      ,
      disabled: this.saving || this.loading || !this.selectedNodeId || !this.selectedCard,
      onclick: () => this.submit()
    }, m("i", {
      className: "fas fa-check"
    }), " ", this.saving ? '提交中...' : '提交预约'))];
  }
}
flarum.reg.add('ziiven-ziven-scheduler', 'forum/modal/SelfBookingModal', SelfBookingModal);